import React, { FC, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useDeepCompareCallback, useDeepCompareMemo } from "use-deep-compare";
import ReactJson from "react-json-view";
import * as _ from "lodash";

import {
  CtraLayout,
  Typography,
  Collapse,
  List,
  Row,
  Col,
  Button,
  Divider,
  PlusOutlined,
  Analytics,
  BarChartOutlined,
  Tabs,
  UnorderedListOutlined
} from "@ctra/components";

import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { Breadcrumbs, DataDescriptorEntity, Enterprise, MetricEntity } from "@ctra/api";
import { GAEvent } from "@ctra/analytics";
import { isDebugMode } from "@ctra/utils";

import { useDataDictionary } from "@base";
import { CompiledRoutes } from "@routes";
import { GACategories, useSavedCharts } from "@analytics";

import { MetricPicker } from "../MetricPicker";
import { ListItem } from "../ListItem";
import { MiniChart } from "../MiniChart";

import styles from "./MetricSelectorPage.module.less";

const { WidgetWrapper, ContentWrapper } = CtraLayout;
const { Paragraph } = Typography;

const {
  components: { Breadcrumb }
} = Breadcrumbs;

const {
  V3: { CategoryIcon }
} = Analytics;

const {
  analytics: {
    config: {
      metric: { displayName },
      search: { pageDescription, cta, title }
    },
    v3: { metricSelector }
  },
  layouts: {
    section: { title: layoutTitle },
    dashboard: { compareCharts }
  }
} = Content;

/**
 * Deduplicate metrics by the lowest family index
 * @param {MetricEntity[]} array
 * @returns {MetricEntity[]}
 */
function deduplicateByLowestFamilyIndex(array: MetricEntity[]): MetricEntity[] {
  const groupedByFamily = _.groupBy(array, (obj) => obj.family || "no-family");

  const result = _.flatMap(groupedByFamily, (familyGroup, key) => {
    if (key === "no-family") {
      return familyGroup;
    }

    return _.minBy(familyGroup, "familyIndex");
  });

  return _.compact(result);
}

/**
 * Metric selector page
 * @return {JSX.Element}
 * @constructor
 */
export const MetricSelectorPage: FC = () => {
  const { dashboard } = useParams<{ dashboard: string }>();
  const { t } = useTranslation();
  const { state } = useLocation();

  const {
    metrics,
    groups: { byCategory },
    meta: { isLoading: dataDictionaryLoading, enums },
    api: { getChartInfo, getDefaultVariant }
  } = useDataDictionary();

  const isCompareChartEntry = !dashboard;

  /**
   * Track which group we are in for breadcrumbs
   */
  const { savedCharts } = useSavedCharts();
  const groupName = _.find(savedCharts, { id: dashboard })?.name;

  /**
   * Track the changes in the metric picker
   */
  const [selectedMetricID, setSelectedMetricID] = useState<MetricEntity["id"]>();

  /**
   * Track the view: "chart" or "list"
   */
  const [view, setView] = useState<string>("chart");

  /**
   * Get BE enums for category which contains the order in which they should be mapped
   */
  const categoryOrder: string[] = _.get(enums, ["MetricCategory"], []);

  /**
   * Configure the confirmed metric
   * @param {MetricEntity["id"]} metricID
   */
  const handleMetricConfirm = (metricID: MetricEntity["id"]) => {
    const { name } = metrics[metricID];

    GAEvent(
      GACategories.configurator,
      "Select metric and route to config",
      JSON.stringify({
        name
      })
    );

    Enterprise.history.push({
      pathname: isCompareChartEntry
        ? CompiledRoutes.app.analytics.compareCharts.withMetric({ source: "metric", objectID: metricID })
        : CompiledRoutes.app.analytics.dashboard.addChart.metric({ dashboard, metricID }),
      state
    });
  };

  /**
   * Sort the categories by the order defined in the enums
   * @type {string[]}
   */
  const categories: string[] = useDeepCompareMemo(() => {
    return _.keys(byCategory).sort((a, b) => {
      const indexA = categoryOrder.indexOf(a);
      const indexB = categoryOrder.indexOf(b);

      return indexA - indexB;
    });
  }, [byCategory, categoryOrder]);

  /**
   * Get items for the list view
   * @type {(category: keyof Record<string, MetricEntity[]>) => {readonly name?: any, readonly family?: any}[]}
   * @todo deduplicate when necessary
   */
  const getListItems: (category: keyof typeof byCategory) => Array<MetricEntity> = useDeepCompareCallback(
    (category) => {
      const metricList = deduplicateByLowestFamilyIndex(_.get(byCategory, category));

      return _.orderBy(metricList, ({ name }) => {
        return t<string>(displayName(name));
      });
    },
    [byCategory]
  );

  /**
   * Get items for the chart view
   * @type {(category) => {metricID: any, variantID: string}[]}
   * @todo deduplicate when necessary
   */
  const getChartItems: (category: keyof typeof byCategory) => Array<{
    metricID: MetricEntity["id"];
    variantID: DataDescriptorEntity["id"];
  }> = useDeepCompareCallback(
    (category) => {
      const metricList = deduplicateByLowestFamilyIndex(_.get(byCategory, category));

      return _.orderBy(
        _.map(metricList, (metric) => {
          const variant = getDefaultVariant(metric.id);

          return {
            metricID: metric.id,
            variantID: variant.id
          };
        }),
        ({ metricID, variantID }) => {
          const { nameToDisplay } = getChartInfo(metricID, variantID);

          return t<string>(displayName(nameToDisplay));
        }
      );
    },
    [byCategory]
  );

  return (
    <WidgetWrapper
      color="neutral"
      loading={dataDictionaryLoading}
      title={
        isCompareChartEntry ? (
          t<string>(compareCharts.title)
        ) : (
          <Paragraph>{t<string>(pageDescription)}</Paragraph>
        )
      }
    >
      {groupName && (
        <Breadcrumb
          path={CompiledRoutes.app.analytics.dashboard.index({
            dashboard
          })}
          title={groupName}
        />
      )}
      {isDebugMode() ? (
        <div onClick={(e) => e.stopPropagation()}>
          <ReactJson
            collapsed
            style={{
              border: "1px dashed #ccc",
              padding: 16,
              margin: "0 0 32px",
              maxHeight: 300,
              overflow: "scroll"
            }}
            src={{
              filePath:
                "packages/apps/enterprise/src/modules/analytics/components/chart-layout/v3/MetricSelectorPage/MetricSelectorPage.tsx"
            }}
          />
        </div>
      ) : null}
      <Tabs onChange={setView}>
        <Tabs.TabPane
          tab={
            <>
              <BarChartOutlined />
              {t<string>(metricSelector.view, { variant: "chart" })}
            </>
          }
          key="chart"
        />
        <Tabs.TabPane
          tab={
            <>
              <UnorderedListOutlined />
              {t<string>(metricSelector.view, { variant: "list" })}
            </>
          }
          key="list"
        />
      </Tabs>
      <ContentWrapper className={styles.ChartPicker} padded={false}>
        <Paragraph>{t<string>(title)}</Paragraph>
        <Divider />
        <Row gutter={[16, 0]} className={styles.MetricPicker}>
          <Col flex={1}>
            <MetricPicker value={selectedMetricID} onChange={setSelectedMetricID} />
          </Col>
          <Col>
            <Button
              size="large"
              disabled={!selectedMetricID}
              type="primary"
              onClick={selectedMetricID ? () => handleMetricConfirm(selectedMetricID) : void 0}
              icon={<PlusOutlined />}
            >
              {t<string>(cta)}
            </Button>
          </Col>
        </Row>
      </ContentWrapper>
      <Collapse
        accordion
        defaultActiveKey={_.split(_.get(state, "openPanels", ""), ",")}
        onChange={(openPanels) => {
          Enterprise.history.push({
            state: {
              openPanels: _.isString(openPanels) ? openPanels : _.join(openPanels, ",")
            }
          });
        }}
        className={styles.Collapse}
        expandIconPosition="right"
      >
        {_.map(categories, (category, idx) => (
          <Collapse.Panel
            key={category}
            data-gtm-category={GACategories.configurator}
            data-gtm-action="Expand category"
            data-gtm-info={JSON.stringify({ category })}
            header={
              <Row className={styles.Header} gutter={[24, 0]} align="middle">
                <CategoryIcon category={category} />
                <Col>
                  {t<string>(layoutTitle(category), {
                    makeDefaultValue: true
                  })}
                </Col>
              </Row>
            }
          >
            {view === "list" ? (
              <List
                dataSource={getListItems(category)}
                renderItem={({ id, name }) => (
                  <ListItem key={id} id={id} name={name} onClick={_.partial(handleMetricConfirm, id)} />
                )}
              />
            ) : (
              <Row gutter={[20, 20]} className={styles.MiniContainer}>
                {_.map(getChartItems(category), ({ metricID, variantID }) => (
                  <Col key={`${metricID}-${variantID}`} span={6}>
                    <MiniChart metric={metricID} variant={variantID} />
                  </Col>
                ))}
              </Row>
            )}
          </Collapse.Panel>
        ))}
      </Collapse>
    </WidgetWrapper>
  );
};
